import { useEffect, useState } from 'react';
import { Row, Col, Statistic, Card, Tag, Typography, Space, Spin, Alert, Button, Slider, InputNumber, Tooltip, message, Modal, Form, Input } from 'antd';
import { UserOutlined, DollarOutlined, RiseOutlined, FallOutlined, GlobalOutlined, PercentageOutlined, WarningOutlined, CheckCircleOutlined, CloseCircleOutlined, InfoCircleOutlined, ClockCircleOutlined, PieChartOutlined, WalletOutlined, ReloadOutlined, BankOutlined } from '@ant-design/icons';
import { useNavigate, Link } from 'react-router-dom';
import { PageHeader } from '../components/PageHeader';
import { FundingStatus } from '../components/FundingStatus';
import { LocCard } from '../components/LocCard';
import { useAuth } from '../app/AuthContext';
import { useTranslation } from 'react-i18next';
import api, { User as UserData, DashboardData, ProfitAllocation } from '../api';
import { userReopenRequestsEndpoints } from '../api/adminReopenRequestsEndpoints';
import { creditLineEndpoints, CreditLineEligibility } from '../api/creditLineEndpoints';
import { formatCurrency } from '../utils/formatters';
import dayjs from 'dayjs';

const { Text } = Typography;

export function Dashboard() {
  const { t } = useTranslation('dashboard');
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [userData, setUserData] = useState<UserData | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [reinvestPercent, setReinvestPercent] = useState(0);
  const [saving, setSaving] = useState(false);
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
  const [withdrawForm] = Form.useForm();
  const [submittingWithdraw, setSubmittingWithdraw] = useState(false);
  const [recentAllocations, setRecentAllocations] = useState<ProfitAllocation[]>([]);
  const [allocationsLoading, setAllocationsLoading] = useState(false);
  const [reopenStatus, setReopenStatus] = useState<any>(null);
  const [requestingReopen, setRequestingReopen] = useState(false);
  
  // S45: One-time withdrawable states
  const [oneTimeModalOpen, setOneTimeModalOpen] = useState(false);
  const [oneTimeAmount, setOneTimeAmount] = useState(0);
  const [savingOneTime, setSavingOneTime] = useState(false);

  // S-B1: Plaid connection status
  const [hasPlaidLinked, setHasPlaidLinked] = useState(false);

  // S-L10: LOC eligibility (3× threshold)
  const [locEligibility, setLocEligibility] = useState<CreditLineEligibility | null>(null);

  useEffect(() => {
    fetchData();
    fetchPlaidStatus();
    // Check reopen status if user is exited
    if (userData?.participation_status === 'exited') {
      fetchReopenStatus();
    }
  }, [userData?.participation_status]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [userResponse, dashboardResponse] = await Promise.all([
        api.users.me(),
        api.dashboard.getDashboard()
      ]);
      setUserData(userResponse.user);
      setDashboardData(dashboardResponse);
      setReinvestPercent(dashboardResponse.reinvest_percent);
      
      // S-L10: Fetch LOC eligibility if user is eligible for earnings
      if (userResponse.user.eligible_for_earnings) {
        try {
          const eligibilityResponse = await creditLineEndpoints.getEligibility();
          setLocEligibility(eligibilityResponse.eligibility);
        } catch (err) {
          console.error('Failed to fetch LOC eligibility:', err);
          setLocEligibility(null);
        }
      } else {
        setLocEligibility(null);
      }
      
      // Fetch recent allocations (last 5)
      setAllocationsLoading(true);
      try {
        const allocationsResponse = await api.allocations.getMyAllocations({ per: 5 });
        setRecentAllocations(allocationsResponse.allocations);
      } catch (err) {
        console.error('Failed to fetch allocations:', err);
      } finally {
        setAllocationsLoading(false);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlaidStatus = async () => {
    try {
      const response = await api.payments.getProfile();
      setHasPlaidLinked(response.has_linked_account);
    } catch (error) {
      console.error('Failed to fetch Plaid status:', error);
      // Silently fail - not critical
    }
  };

  const fetchReopenStatus = async () => {
    try {
      const status = await userReopenRequestsEndpoints.getStatus();
      setReopenStatus(status);
    } catch (error) {
      console.error('Failed to fetch reopen status:', error);
    }
  };

  const handleRequestReopen = () => {
    Modal.confirm({
      title: 'Request to Reopen Account',
      icon: <ReloadOutlined />,
      content: (
        <Space orientation="vertical">
          <Text>
            Reopening your account requires admin approval and a new funding requirement.
            Once approved, you'll need to fund your account before you can participate in profit allocations again.
          </Text>
          <Text type="secondary">
            Your historical data will be preserved, but you'll start a new participation period.
          </Text>
        </Space>
      ),
      okText: 'Submit Request',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          setRequestingReopen(true);
          await userReopenRequestsEndpoints.requestReopen();
          message.success('Reopen request submitted successfully. An admin will review your request.');
          fetchReopenStatus();
        } catch (error: any) {
          message.error(error.message || 'Failed to submit reopen request');
        } finally {
          setRequestingReopen(false);
        }
      },
    });
  };

  const handleSaveReinvestPercent = async () => {
    // Validate
    if (reinvestPercent < 0 || reinvestPercent > 100) {
      message.error(t('profitSettings.invalidPercentage'));
      return;
    }

    try {
      setSaving(true);
      await api.users.updateReinvestPercent(reinvestPercent);
      await fetchData();
      message.success(t('profitSettings.saveSuccess'));
    } catch (err: any) {
      console.error('Failed to update reinvest percent:', err);
      message.error(err.message || t('profitSettings.saveError'));
      // Rollback on error
      if (dashboardData) {
        setReinvestPercent(dashboardData.reinvest_percent);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleReinvestPercentChange = (value: number | null) => {
    if (value === null) return;
    const newValue = Math.max(0, Math.min(100, value));
    setReinvestPercent(newValue);
  };

  const calculateTimeUntilSettlement = (nextSettlement: string) => {
    const now = new Date();
    const settlement = new Date(nextSettlement);
    const diff = settlement.getTime() - now.getTime();
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${days}d ${hours}h ${minutes}m`;
  };

  const calculateLivePreview = () => {
    if (!dashboardData) return { reinvest: 0, withdraw: 0 };
    
    const profitCents = dashboardData.profit_week_cents;
    const reinvestAmount = Math.round((profitCents * reinvestPercent) / 100);
    const withdrawAmount = profitCents - reinvestAmount;
    
    return {
      reinvest: reinvestAmount / 100,
      withdraw: withdrawAmount / 100
    };
  };

  const handleQuickPercentage = (percent: number) => {
    const withdrawableCents = userData?.withdrawable_cents || 0;
    const amount = Math.round((withdrawableCents * percent) / 100);
    withdrawForm.setFieldsValue({ amount_cents: amount });
  };

  const handleWithdrawSubmit = async (values: { amount_cents: number; user_note?: string }) => {
    try {
      setSubmittingWithdraw(true);
      await api.withdrawals.create(values);
      message.success(t('withdrawModal.submitSuccess'));
      setWithdrawModalOpen(false);
      withdrawForm.resetFields();
      await fetchData(); // Refresh to update withdrawable balance
    } catch (error: any) {
      console.error('Failed to submit withdrawal:', error);
      message.error(error.message || t('withdrawModal.submitError'));
    } finally {
      setSubmittingWithdraw(false);
    }
  };

  const openWithdrawModal = () => {
    withdrawForm.resetFields();
    setWithdrawModalOpen(true);
  };

  // S45: One-time withdrawable handlers
  const openOneTimeModal = () => {
    const currentAmount = (userData?.one_time_withdrawable_amount_cents || 0) / 100;
    setOneTimeAmount(currentAmount);
    setOneTimeModalOpen(true);
  };

  const handleOneTimeAmountChange = (value: number | null) => {
    if (value === null) return;
    const profitDollars = (userData?.profit_week_cents || 0) / 100;
    const clampedValue = Math.max(0, Math.min(profitDollars, value));
    setOneTimeAmount(clampedValue);
  };

  const handleSaveOneTime = async () => {
    try {
      setSavingOneTime(true);
      const amountCents = Math.round(oneTimeAmount * 100);
      await api.users.setOneTimeWithdrawable(amountCents);
      message.success(t('oneTimeWithdraw.saveSuccess'));
      setOneTimeModalOpen(false);
      await fetchData(); // Refresh to update user data
    } catch (error: any) {
      console.error('Failed to set one-time withdrawable:', error);
      message.error(error.message || t('oneTimeWithdraw.saveError'));
    } finally {
      setSavingOneTime(false);
    }
  };

  const handleClearOneTime = async () => {
    try {
      setSavingOneTime(true);
      await api.users.clearOneTimeWithdrawable();
      message.success(t('oneTimeWithdraw.clearSuccess'));
      setOneTimeModalOpen(false);
      await fetchData(); // Refresh to update user data
    } catch (error: any) {
      console.error('Failed to clear one-time withdrawable:', error);
      message.error(error.message || t('oneTimeWithdraw.saveError'));
    } finally {
      setSavingOneTime(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  const livePreview = calculateLivePreview();
  const isEligible = dashboardData?.eligible_for_earnings ?? userData?.eligible_for_earnings ?? false;

  return (
    <div>
      <PageHeader
        title={t('welcome', { name: userData?.full_name || user?.full_name || 'User' })}
        description={t('description')}
        extra={dashboardData && (
          <Space>
            <ClockCircleOutlined />
            <Text type="secondary">
              {t('nextSettlement', { time: calculateTimeUntilSettlement(dashboardData.next_settlement) })}
            </Text>
          </Space>
        )}
      />

      {/* Admin Base Not Initialized Banner */}
      {isAdmin && userData && !userData.base_principal_initialized_at && (
        <Alert
          title={t('adminAlert.title')}
          description={
            <Space orientation="vertical" size="small">
              <span>
                {t('adminAlert.description')}
              </span>
              <Button
                type="primary"
                icon={<WarningOutlined />}
                onClick={() => navigate('/admin/bootstrap')}
              >
                {t('adminAlert.button')}
              </Button>
            </Space>
          }
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          style={{ marginBottom: 24 }}
          closable={false}
        />
      )}

      {/* S41: Account Closure Status Banner */}
      {!isAdmin && userData && userData.participation_status && (userData.participation_status === 'pending_close' || userData.participation_status === 'exited') && (
        <div style={{ marginBottom: 24 }}>
          <Alert
            type={userData.participation_status === 'pending_close' ? 'warning' : 'info'}
            showIcon
            icon={userData.participation_status === 'pending_close' ? <ClockCircleOutlined /> : <InfoCircleOutlined />}
            title={
              <Space>
                <strong>
                  {userData.participation_status === 'pending_close' 
                    ? t('closureStatus.pendingTitle', 'Account Closure Pending')
                    : t('closureStatus.exitedTitle', 'Account Closed')}
                </strong>
              </Space>
            }
            description={
              <Space orientation="vertical" size="small" style={{ width: '100%' }}>
                <Text>
                  {userData.participation_status === 'pending_close'
                    ? t('closureStatus.pendingMessage', 'Your account closure request is being processed. You will receive one more profit allocation in the next weekly settlement, after which your account will be closed and a final withdrawal will be created.')
                    : t('closureStatus.exitedMessage', 'Your account has been closed and you are no longer participating in the pool. No new profits will be allocated to you.')}
                </Text>
                {userData.participation_status === 'exited' && (
                  <>
                    <Text type="secondary">
                      {t('closureStatus.historyAvailable', 'You can still view your historical contributions, settlements, and withdrawals below.')}
                    </Text>
                    {reopenStatus && reopenStatus.reopen_request?.status === 'pending_admin_review' ? (
                      <Alert
                        type="info"
                        title="Reopen Request Pending"
                        description="Your request to reopen your account is being reviewed by an admin. You will be notified once it has been approved or rejected."
                        showIcon
                        style={{ marginTop: 8 }}
                      />
                    ) : reopenStatus?.can_request_reopen && (
                      <Button
                        type="primary"
                        icon={<ReloadOutlined />}
                        onClick={handleRequestReopen}
                        loading={requestingReopen}
                        style={{ marginTop: 8 }}
                      >
                        Request to Reopen Account
                      </Button>
                    )}
                  </>
                )}
              </Space>
            }
          />
        </div>
      )}

      {/* S40: Funding Status Card - Show when user is not yet eligible */}
      {!isAdmin && userData && !userData.eligible_for_earnings && (
        <div style={{ marginBottom: 24 }}>
          <FundingStatus onStatusChange={fetchData} />
        </div>
      )}

      {/* User Info Card */}
      {userData && (
        <Card style={{ marginBottom: 24 }} variant="borderless">
          <Space orientation="vertical" size="small" style={{ width: '100%' }}>
            <Space>
              <UserOutlined style={{ fontSize: 16, color: '#1677ff' }} />
              <Text strong>{t('userInfo.email')}:</Text>
              <Text>{userData.email}</Text>
            </Space>
            <Space>
              <GlobalOutlined style={{ fontSize: 16, color: userData.country === 'USA' ? '#1677ff' : '#9254de' }} />
              <Text strong>{t('userInfo.country')}:</Text>
              <Tag color={userData.country === 'USA' ? 'blue' : 'purple'}>
                {userData.country === 'USA' ? t('userInfo.unitedStates') : t('userInfo.nonUsa')}
              </Tag>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t('userInfo.countryNote')}
              </Text>
            </Space>
            <Space>
              {userData.eligible_for_earnings ? (
                <CheckCircleOutlined style={{ fontSize: 16, color: '#52c41a' }} />
              ) : (
                <CloseCircleOutlined style={{ fontSize: 16, color: '#faad14' }} />
              )}
              <Text strong>{t('userInfo.earningEligibility')}:</Text>
              <Tag color={userData.eligible_for_earnings ? 'green' : 'orange'}>
                {userData.eligibility_status === 'eligible' ? t('userInfo.eligible') : t('userInfo.notEligible')}
              </Tag>
              {!userData.eligible_for_earnings && userData.contribution_shortfall_cents > 0 && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t('userInfo.shortfallNote', { amount: formatCurrency(userData.contribution_shortfall_cents) })}
                </Text>
              )}
            </Space>
          </Space>
        </Card>
      )}

      {/* S-L10: Line of Credit Card - Only show when user has reached 3× threshold */}
      {userData && userData.eligible_for_earnings && locEligibility && locEligibility.eligible && (
        <div style={{ marginBottom: 24 }}>
          <LocCard />
        </div>
      )}

      {/* Financial Stats Overview */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card variant="borderless">
            <Statistic
              title={t('stats.basePrincipal')}
              value={formatCurrency(userData?.base_principal_cents || 0)}
              prefix={<DollarOutlined />}
              styles={{ content: { color: '#3f8600' } }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card variant="borderless">
            <Statistic
              title={
                <Space>
                  <span>{t('stats.ownership')}</span>
                  <Tooltip title={t('stats.ownershipTooltip')}>
                    <InfoCircleOutlined style={{ fontSize: 12 }} />
                  </Tooltip>
                </Space>
              }
              value={dashboardData?.ownership_percent || '0.00%'}
              prefix={<PieChartOutlined />}
              styles={{ content: { color: '#1677ff' } }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card variant="borderless">
            <Statistic
              title={
                <Space style={{ width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{t('stats.thisWeeksProfit')}</span>
                  {userData && userData.participation_status === 'active' && userData.profit_week_cents > 0 && (
                    <Button
                      type="link"
                      size="small"
                      onClick={openOneTimeModal}
                    >
                      {t('oneTimeWithdraw.button')}
                    </Button>
                  )}
                </Space>
              }
              value={formatCurrency(userData?.profit_week_cents || 0)}
              prefix={(userData?.profit_week_cents || 0) >= 0 ? <RiseOutlined /> : <FallOutlined />}
              styles={{ content: { color: (userData?.profit_week_cents || 0) >= 0 ? '#52c41a' : '#cf1322' } }}
            />
            {/* S45: Show pending one-time amount if set */}
            {userData && (userData.one_time_withdrawable_amount_cents || 0) > 0 && (
              <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed #d9d9d9' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t('oneTimeWithdraw.pendingLabel')}
                </Text>
                <br />
                <Text strong style={{ fontSize: 14, color: '#fa8c16' }}>
                  {formatCurrency(userData.one_time_withdrawable_amount_cents!)}
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {t('oneTimeWithdraw.remainingNote')}
                </Text>
              </div>
            )}
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card variant="borderless">
            <Statistic
              title={
                <Space style={{ width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{t('stats.withdrawable')}</span>
                  <Button
                    type="primary"
                    icon={<WalletOutlined />}
                    onClick={openWithdrawModal}
                    disabled={!userData || userData.withdrawable_cents === 0}
                    size="small"
                  >
                    {t('stats.requestButton')}
                  </Button>
                </Space>
              }
              value={formatCurrency(userData?.withdrawable_cents || 0)}
              prefix={<DollarOutlined />}
              styles={{ content: { color: 'rgb(250, 140, 22)' } }}
            />
          </Card>
        </Col>
      </Row>

      {/* S39: Performance Summary */}
      {dashboardData?.performance_summary && (
        <Card
          title={
            <Space>
              <RiseOutlined style={{ color: '#1677ff' }} />
              <span>{t('performance.title')}</span>
            </Space>
          }
          variant="borderless"
          style={{ marginBottom: 24 }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title={t('performance.week')}
                  value={formatCurrency(dashboardData.performance_summary.week.amount * 100)}
                  suffix={
                    <Text 
                      type={dashboardData.performance_summary.week.percent >= 0 ? 'success' : 'danger'}
                      style={{ fontSize: 14 }}
                    >
                      {dashboardData.performance_summary.week.percent >= 0 ? '+' : ''}
                      {dashboardData.performance_summary.week.percent.toFixed(2)}%
                    </Text>
                  }
                  prefix={dashboardData.performance_summary.week.percent >= 0 ? <RiseOutlined /> : <FallOutlined />}
                  styles={{ 
                    content: { 
                      color: dashboardData.performance_summary.week.percent >= 0 ? '#52c41a' : '#cf1322' 
                    } 
                  }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title={t('performance.month')}
                  value={formatCurrency(dashboardData.performance_summary.month.amount * 100)}
                  suffix={
                    <Text 
                      type={dashboardData.performance_summary.month.percent >= 0 ? 'success' : 'danger'}
                      style={{ fontSize: 14 }}
                    >
                      {dashboardData.performance_summary.month.percent >= 0 ? '+' : ''}
                      {dashboardData.performance_summary.month.percent.toFixed(2)}%
                    </Text>
                  }
                  prefix={dashboardData.performance_summary.month.percent >= 0 ? <RiseOutlined /> : <FallOutlined />}
                  styles={{ 
                    content: { 
                      color: dashboardData.performance_summary.month.percent >= 0 ? '#52c41a' : '#cf1322' 
                    } 
                  }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title={t('performance.ytd')}
                  value={formatCurrency(dashboardData.performance_summary.ytd.amount * 100)}
                  suffix={
                    <Text 
                      type={dashboardData.performance_summary.ytd.percent >= 0 ? 'success' : 'danger'}
                      style={{ fontSize: 14 }}
                    >
                      {dashboardData.performance_summary.ytd.percent >= 0 ? '+' : ''}
                      {dashboardData.performance_summary.ytd.percent.toFixed(2)}%
                    </Text>
                  }
                  prefix={dashboardData.performance_summary.ytd.percent >= 0 ? <RiseOutlined /> : <FallOutlined />}
                  styles={{ 
                    content: { 
                      color: dashboardData.performance_summary.ytd.percent >= 0 ? '#52c41a' : '#cf1322' 
                    } 
                  }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title={t('performance.allTime')}
                  value={formatCurrency(dashboardData.performance_summary.all.amount * 100)}
                  suffix={
                    <Text 
                      type={dashboardData.performance_summary.all.percent >= 0 ? 'success' : 'danger'}
                      style={{ fontSize: 14 }}
                    >
                      {dashboardData.performance_summary.all.percent >= 0 ? '+' : ''}
                      {dashboardData.performance_summary.all.percent.toFixed(2)}%
                    </Text>
                  }
                  prefix={dashboardData.performance_summary.all.percent >= 0 ? <RiseOutlined /> : <FallOutlined />}
                  styles={{ 
                    content: { 
                      color: dashboardData.performance_summary.all.percent >= 0 ? '#52c41a' : '#cf1322' 
                    } 
                  }}
                />
              </Card>
            </Col>
          </Row>
        </Card>
      )}

      {/* Reinvestment Settings with Live Preview */}
      <Card
        title={
          <Space>
            <span>{t('profitSettings.title')}</span>
            {!isEligible && (
              <Tag color="orange">{t('profitSettings.notEligibleTag')}</Tag>
            )}
          </Space>
        }
        variant="borderless"
        style={{ marginBottom: 24 }}
      >
        <Space orientation="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Space>
              <PercentageOutlined style={{ fontSize: 20, color: '#1677ff' }} />
              <Text strong>{t('profitSettings.reinvestmentPercentage')}:</Text>
              <Tooltip title={t('profitSettings.reinvestmentTooltip')}>
                <InfoCircleOutlined style={{ cursor: 'pointer' }} />
              </Tooltip>
            </Space>
            
            <Row gutter={16} align="middle">
              <Col flex="auto">
                <Slider
                  min={0}
                  max={100}
                  value={reinvestPercent}
                  onChange={handleReinvestPercentChange}
                  disabled={!isEligible || saving}
                  tooltip={{ formatter: (value) => `${value}%` }}
                />
              </Col>
              <Col>
                <InputNumber
                  min={0}
                  max={100}
                  value={reinvestPercent}
                  onChange={handleReinvestPercentChange}
                  disabled={!isEligible || saving}
                  formatter={value => `${value}%`}
                  parser={value => Number(value?.replace('%', '')) || 0}
                  style={{ width: 80 }}
                />
              </Col>
              <Col>
                <Button
                  type="primary"
                  onClick={handleSaveReinvestPercent}
                  loading={saving}
                  disabled={!isEligible || saving || reinvestPercent === dashboardData?.reinvest_percent}
                >
                  {t('profitSettings.saveButton')}
                </Button>
              </Col>
            </Row>

            <Space style={{ marginBottom: 8 }}>
              {reinvestPercent !== dashboardData?.reinvest_percent && (
                <Tag color="orange">{t('profitSettings.unsavedChanges')}</Tag>
              )}
              {!isEligible && <Tag color="orange">{t('profitSettings.notEligibleTag')}</Tag>}
            </Space>
          </div>

          {/* Live Preview */}
          <div>
            <Text strong style={{ display: 'block', marginBottom: 12 }}>
              {t('profitSettings.previewTitle')}
            </Text>
            <Row gutter={[12, 12]}>
              <Col xs={24} sm={12}>
                <Card 
                  size="small" 
                  style={{ 
                    backgroundColor: '#f6ffed', 
                    border: '1px solid #b7eb8f'
                  }}
                >
                  <Space orientation="vertical" size={2} style={{ width: '100%' }}>
                    <Text style={{ fontSize: 13, fontWeight: 600, color: '#52c41a' }}>
                      {t('profitSettings.basePrincipal')}
                    </Text>
                    <Text strong style={{ fontSize: 22, color: '#3f8600' }}>
                      ${livePreview.reinvest.toFixed(2)}
                    </Text>
                    <Text style={{ fontSize: 12, color: '#595959' }}>
                      {t('profitSettings.addedOnSettlement')}
                    </Text>
                  </Space>
                </Card>
              </Col>
              <Col xs={24} sm={12}>
                <Card 
                  size="small" 
                  style={{ 
                    backgroundColor: '#fff7e6', 
                    border: '1px solid #ffd591'
                  }}
                >
                  <Space orientation="vertical" size={2} style={{ width: '100%' }}>
                    <Text style={{ fontSize: 13, fontWeight: 600, color: '#fa8c16' }}>
                      {t('profitSettings.withdrawable')}
                    </Text>
                    <Text strong style={{ fontSize: 22, color: '#d46b08' }}>
                      ${livePreview.withdraw.toFixed(2)}
                    </Text>
                    <Text style={{ fontSize: 12, color: '#595959' }}>
                      {t('profitSettings.addedOnSettlement')}
                    </Text>
                  </Space>
                </Card>
              </Col>
            </Row>
          </div>

          {!isEligible && userData && userData.contribution_shortfall_cents > 0 && (
            <Alert
              title={t('profitSettings.notEligibleAlert.title')}
              description={t('profitSettings.notEligibleAlert.description', { 
                amount: formatCurrency(userData.contribution_shortfall_cents) 
              })}
              type="warning"
              showIcon
            />
          )}
        </Space>
      </Card>

      {/* Recent Allocations */}
      <Card 
        title={t('recentAllocations.title')}
        variant="borderless"
        extra={
          recentAllocations.length > 0 && (
            <Button type="link" onClick={() => navigate('/history')}>
              {t('recentAllocations.viewAll')}
            </Button>
          )
        }
      >
        {allocationsLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin />
          </div>
        ) : recentAllocations.length > 0 ? (
          <>
            <table style={{ width: '100%' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #f0f0f0' }}>
                  <th style={{ textAlign: 'left', padding: '12px 8px', fontWeight: 600 }}>{t('recentAllocations.date')}</th>
                  <th style={{ textAlign: 'right', padding: '12px 8px', fontWeight: 600 }}>{t('recentAllocations.gross')}</th>
                  <th style={{ textAlign: 'right', padding: '12px 8px', fontWeight: 600 }}>{t('recentAllocations.companyCut')}</th>
                  <th style={{ textAlign: 'right', padding: '12px 8px', fontWeight: 600 }}>{t('recentAllocations.net')}</th>
                </tr>
              </thead>
              <tbody>
                {recentAllocations.map((allocation, index) => (
                  <tr 
                    key={allocation.id} 
                    style={{ borderBottom: index < recentAllocations.length - 1 ? '1px solid #f0f0f0' : 'none' }}
                  >
                    <td style={{ padding: '12px 8px' }}>
                      {dayjs(allocation.profit_entry_date).format('MMM D, YYYY')}
                    </td>
                    <td style={{ textAlign: 'right', padding: '12px 8px' }}>
                      {formatCurrency(allocation.gross_share_cents)}
                    </td>
                    <td style={{ textAlign: 'right', padding: '12px 8px' }}>
                      {formatCurrency(allocation.company_cut_cents)}
                    </td>
                    <td style={{ textAlign: 'right', padding: '12px 8px', fontWeight: 600, color: '#52c41a' }}>
                      {formatCurrency(allocation.net_share_cents)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Text type="secondary">
              {t('recentAllocations.noAllocations')}
            </Text>
          </div>
        )}
      </Card>

      {/* Withdrawal Request Modal */}
      <Modal
        title={t('withdrawModal.title')}
        open={withdrawModalOpen}
        onCancel={() => setWithdrawModalOpen(false)}
        footer={null}
        destroyOnHidden
      >
        {/* Plaid Connection Status */}
        <Alert
          message={
            <Space>
              <BankOutlined />
              {hasPlaidLinked ? (
                <>
                  <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  <span>Plaid Connected</span>
                </>
              ) : (
                <>
                  <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                  <span>No Plaid Account Connected</span>
                </>
              )}
            </Space>
          }
          description={
            !hasPlaidLinked && (
              <span>
                Large withdrawals require Plaid. <Link to="/settings">Connect your bank account</Link> in settings.
              </span>
            )
          }
          type={hasPlaidLinked ? 'success' : 'warning'}
          showIcon={false}
          style={{ marginBottom: 16 }}
        />

        <Alert
          title={t('withdrawModal.processingTime.title')}
          description={t('withdrawModal.processingTime.description')}
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Form
          form={withdrawForm}
          layout="vertical"
          onFinish={handleWithdrawSubmit}
          initialValues={{ amount_cents: 0 }}
        >
          <Form.Item
            label={
              <Space>
                <span>{t('withdrawModal.amount')}</span>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t('withdrawModal.available', { amount: formatCurrency(userData?.withdrawable_cents || 0) })}
                </Text>
              </Space>
            }
            name="amount_cents"
            rules={[
              { required: true, message: t('withdrawModal.amountRequired') },
              {
                validator: (_, value) => {
                  if (value <= 0) {
                    return Promise.reject(t('withdrawModal.amountGreaterThanZero'));
                  }
                  if (value > (userData?.withdrawable_cents || 0)) {
                    return Promise.reject(t('withdrawModal.amountExceedsBalance'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              max={userData?.withdrawable_cents || 0}
              formatter={value => {
                const dollars = (Number(value) || 0) / 100;
                return `$ ${dollars.toFixed(2)}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
              }}
              parser={value => {
                const cleaned = value?.replace(/\$\s?|(,*)/g, '') || '0';
                return Math.round(Number(cleaned) * 100);
              }}
              placeholder={t('withdrawModal.placeholder')}
            />
          </Form.Item>

          <Space style={{ marginBottom: 16, width: '100%' }} wrap>
            <Button size="small" onClick={() => handleQuickPercentage(25)}>25%</Button>
            <Button size="small" onClick={() => handleQuickPercentage(50)}>50%</Button>
            <Button size="small" onClick={() => handleQuickPercentage(75)}>75%</Button>
            <Button size="small" onClick={() => handleQuickPercentage(100)}>100%</Button>
          </Space>

          <Form.Item
            label={t('withdrawModal.noteLabel')}
            name="user_note"
          >
            <Input.TextArea
              rows={3}
              placeholder={t('withdrawModal.notePlaceholder')}
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setWithdrawModalOpen(false)}>
                {t('withdrawModal.cancelButton')}
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={submittingWithdraw}
                icon={<WalletOutlined />}
              >
                {t('withdrawModal.submitButton')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* S45: One-time Withdrawable Modal */}
      <Modal
        title={t('oneTimeWithdraw.modalTitle')}
        open={oneTimeModalOpen}
        onCancel={() => setOneTimeModalOpen(false)}
        footer={null}
        destroyOnHidden
      >
        <Space orientation="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Text type="secondary">{t('oneTimeWithdraw.currentProfit')}:</Text>
            <br />
            <Text strong style={{ fontSize: 18 }}>
              {formatCurrency(userData?.profit_week_cents || 0)}
            </Text>
          </div>

          <div>
            <Text strong>{t('oneTimeWithdraw.amountLabel')}:</Text>
            <br />
            <Row gutter={16} align="middle" style={{ marginTop: 8 }}>
              <Col flex="auto">
                <Slider
                  min={0}
                  max={(userData?.profit_week_cents || 0) / 100}
                  step={0.01}
                  value={oneTimeAmount}
                  onChange={handleOneTimeAmountChange}
                  tooltip={{
                    formatter: (value) => `$${(value || 0).toFixed(2)}`
                  }}
                />
              </Col>
              <Col>
                <InputNumber
                  min={0}
                  max={(userData?.profit_week_cents || 0) / 100}
                  step={0.01}
                  value={oneTimeAmount}
                  onChange={handleOneTimeAmountChange}
                  formatter={value => `$ ${(Number(value) || 0).toFixed(2)}`}
                  parser={value => parseFloat(value?.replace(/\$\s?|(,*)/g, '') || '0')}
                  style={{ width: 120 }}
                />
              </Col>
            </Row>
          </div>

          <Alert
            message={t('oneTimeWithdraw.helpText')}
            type="info"
            showIcon
          />

          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            {((userData?.one_time_withdrawable_amount_cents || 0) > 0) && (
              <Button
                onClick={handleClearOneTime}
                loading={savingOneTime}
              >
                {t('oneTimeWithdraw.clearButton')}
              </Button>
            )}
            <Button onClick={() => setOneTimeModalOpen(false)}>
              {t('oneTimeWithdraw.cancelButton')}
            </Button>
            <Button
              type="primary"
              onClick={handleSaveOneTime}
              loading={savingOneTime}
            >
              {t('oneTimeWithdraw.saveButton')}
            </Button>
          </Space>
        </Space>
      </Modal>
    </div>
  );
}

export default Dashboard;